import classnames from 'classnames'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import ImageDomain from '@src/quince-site-common/src/domains/Image'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductMedia from '@src/quince-site-common/src/domains/ProductMedia'
import ProductVariant from '@src/quince-site-common/src/domains/ProductVariant'
import VideoDomain from '@src/quince-site-common/src/domains/Video'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import usePx from '@src/quince-site-common/src/hooks/usePx'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { isImage, isProductImage, isProductVideo, isVideo } from '@src/quince-site-common/src/utils/typeGuards'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent, trackGAEvent, trackInternalEvent } from '@src/telemetry/track'

import ChevronIcon from '../chevronIcon/ChevronIcon'
import Swiper, { NAV_POSITION, SwiperProps } from '../custom-swiper'
import { Image } from '../image'
import { Modal } from '../modal'
import Video, { VIDEO_LOADING_TYPE, VIDEO_PRELOAD_TYPE } from '../video'
import VideoPlaybackIcon from '../video/icons/videoPlayIcon.inline.svg'

import {
  handleImageContainerMouseLeave,
  handleImageContainerMouseMove,
  productGalleryScrollEventTrigger,
} from './SliderImages.helper'
import styles from './sliderImages.module.scss'

export interface SliderImagesProps {
  className?: string
  renderMainImageOverlay?: (index: number) => ReactNode
  media: ProductMedia[]
  hasSelectedOptionImages?: boolean
  leftSliderImage?: string
  leftContainerClassname?: string
  rightContainerClassname?: string
  leftContainerSliderClassname?: string
  hideProductThumbnails?: boolean
  sliderIconClassName?: string
  variants?: ProductVariant[]
  product?: Product
  shouldLoop?: boolean
  pdpHeroImageId?: string
  videoTrackingProperties?: Record<string, unknown>
}

const COMMON_SLIDER_SETTINGS: SwiperProps = {
  // @i18n-ignore
  id: 'slider-images-common-slider',
  children: [],
  showDots: false,
  infinite: { loop: true, loopedSlides: 1 },
  isVertical: true,
  showNavigator: false,
  numberOfVisibleSlides: 5,
}

const MOBILE_SLIDER_SETTINGS: SwiperProps = {
  ...COMMON_SLIDER_SETTINGS,
  numberOfVisibleSlides: 1,
  isVertical: false,
  infinite: { loop: true, loopedSlides: 1 },
}

const DESKTOP_SLIDER_SETTINGS: SwiperProps = {
  ...COMMON_SLIDER_SETTINGS,
  virtualOptions: {
    enabled: true,
  },
  // TODO: Change this to true when slider is fixed for image thumbnails on desktop
  showNavigator: false,
  isChevronIcon: true,
  navPosition: NAV_POSITION.VERTICAL,
}

const getSliderSettings = (media: (ImageDomain | VideoDomain)[], isMobile: boolean) => {
  const mobileSliderSettings =
    media.length === 1
      ? MOBILE_SLIDER_SETTINGS
      : {
          ...MOBILE_SLIDER_SETTINGS,
          variableWidth: true,
        }
  return isMobile ? mobileSliderSettings : DESKTOP_SLIDER_SETTINGS
}

const SliderImages = ({
  className,
  media = [],
  renderMainImageOverlay,
  hasSelectedOptionImages,
  leftSliderImage,
  leftContainerClassname,
  leftContainerSliderClassname,
  hideProductThumbnails,
  sliderIconClassName,
  rightContainerClassname,
  variants,
  product,
  shouldLoop = true,
  pdpHeroImageId = '',
  videoTrackingProperties,
}: SliderImagesProps) => {
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const [index, setIndex] = useState(0)
  const [showImagePopup, setShowImagePopup] = useState(false)
  const enableHighQualityImageDownloadOnHover = true
  const [isHoverContainerEnabled, setIsHoverContainerEnabled] = useState(false)

  const videoIndex = media.findIndex(md => isProductVideo(md))

  const classes = classnames(styles.container, className, {
    [styles.hideThumbnailsContainer as string]: hideProductThumbnails,
  })

  // Resetting the index in case the image changes.
  const id = media.reduce((acc, md) => {
    let val = ''
    if (isProductImage(md)) {
      val = md.image.url
    } else if (isProductVideo(md)) {
      val = md.video.url
    }
    return acc + val
  }, '')

  useEffect(() => {
    setIndex(0)
  }, [id])

  useEffect(() => {
    setIsHoverContainerEnabled(true)
  }, [])

  const settings = getSliderSettings(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    media.map(i => (isProductVideo(i) ? i.video : i.image)).filter(pi => !!pi) as ImageDomain[],
    isMobile,
  )

  const setActiveImage = (imgIdx: number) => {
    setIndex(imgIdx)
  }
  const handleImageClick = (index: number) => {
    setActiveImage(index)
    setShowImagePopup(true)
  }

  const closeImagePopup = () => {
    setShowImagePopup(false)
  }

  const imageContainerRef = useRef<HTMLDivElement>(null)
  const hoverContainerRef = useRef<HTMLDivElement>(null)
  const sideBoxParentContainerRef = useRef<HTMLDivElement>(null)
  const sideBoxImageContainerRef = useRef<HTMLDivElement>(null)

  const handleImageContainerMouseEnter = () => {
    trackInternalEvent(TELEMETRY_EVENT_NAMES.ZOOM_PRODUCT_IMAGE, {
      context: {
        productProperties: product
          ? getProductPropertiesForTracking(product, variants?.[0]?.shopifyVariantId?.toString())
          : {},
      },
    })
    trackGAEvent(TELEMETRY_EVENT_NAMES.ZOOM_PRODUCT_IMAGE, {
      context: {
        productProperties: product
          ? getProductPropertiesForTracking(product, variants?.[0]?.shopifyVariantId?.toString())
          : {},
      },
    })
  }

  const getPxFromRem: (withRem: number, withUnits: boolean) => string | number = usePx()

  const selectedProductMedia = media[index]
  const mainMedia =
    selectedProductMedia &&
    (isProductImage(selectedProductMedia) ? selectedProductMedia.image : selectedProductMedia.video)

  const iconClasses = classnames({
    [`${sliderIconClassName}`]: true,
    ['slider-images-icon-wrapper']: true,
  })
  const handleOnScrollEvent = useCallback(
    () => productGalleryScrollEventTrigger(product, variants?.[0]?.shopifyVariantId?.toString()),
    [product, variants],
  )

  return (
    <>
      <div className={classes}>
        {hideProductThumbnails ? null : (
          <div
            data-testid={`swiper-left-container`}
            className={classnames(styles.container__left, leftContainerClassname)}
          >
            <Swiper
              id={id}
              key={id}
              className={classnames(
                styles.container__left__carousel,
                styles.container__left__slider,
                leftContainerSliderClassname,
              )}
              prevIconClass={iconClasses}
              nextIconClass={iconClasses}
              {...settings}
              slideClassName={styles.container__left__slider__modal__sliderWrapper}
              infinite={{ loop: isMobile && shouldLoop, loopedSlides: 1 }}
              handleOnScrollEvent={handleOnScrollEvent}
            >
              {media.map((med, index) => {
                return (
                  <div
                    className={classnames(
                      styles.container__left__slider__image,
                      media.length === 1 && styles.container__left__slider__singleImage,
                      leftSliderImage,
                    )}
                    key={isProductImage(med) ? med.image.url : med.video.url}
                    onClick={
                      isMobile
                        ? () => {
                            handleImageClick(index)
                          }
                        : undefined
                    }
                    onMouseEnter={() => {
                      setActiveImage(index)
                      trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_VIEWED, {
                        product_id: variants?.[0]?.productId,
                        context: {
                          productProperties: product
                            ? getProductPropertiesForTracking(product, variants?.[0]?.shopifyVariantId?.toString())
                            : {},
                        },
                      })
                    }}
                  >
                    {/* The following check only renders the first image in mobile view, any not the others to reduce the SSR markup. */}
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {((isMobile && index === 0) || isHoverContainerEnabled) && isProductImage(med) && med.image && (
                      <>
                        <Image
                          alt={med.image.alt ?? med.image.title}
                          {...ImageDomain.makeImageProps(med.image)}
                          // @i18n-ignore
                          loading={isMobile ? 'eager' : 'lazy'}
                          hasDynamicImage
                          maxWidth={getMaxWidthInPx({
                            maxWidthDesktopInRem: styles.sliderImageDesktopWidth as string,
                            maxWidthMobileInRem: styles.sliderImageMobileWidth as string,
                            deviceType: deviceInfo.deviceType,
                          })}
                          // @i18n-ignore
                          fetchPriority={isMobile && index === 0 ? 'high' : 'auto'}
                        />
                        {renderMainImageOverlay && isMobile && index === 0 ? renderMainImageOverlay(index) : null}
                      </>
                    )}
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {isProductVideo(med) && med.video && (
                      <div className={styles.container__left__slider__image__videoPoster}>
                        <Image
                          alt={med.videoPoster?.title}
                          {...ImageDomain.makeImageProps(med.videoPoster)}
                          // @i18n-ignore
                          loading={'eager'}
                          hasDynamicImage
                          maxWidth={getMaxWidthInPx({
                            maxWidthDesktopInRem: styles.sliderImageDesktopWidth as string,
                            maxWidthMobileInRem: styles.sliderImageMobileWidth as string,
                            deviceType: deviceInfo.deviceType,
                          })}
                        />
                        <div className={styles.container__left__slider__image__videoPoster__playbackIconContainer}>
                          <VideoPlaybackIcon />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </Swiper>
          </div>
        )}
        <div
          data-testid={'swiper-right-container'}
          className={classnames(styles.container__right, rightContainerClassname)}
        >
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {mainMedia && !mainMedia.url.includes('video') && isImage(mainMedia) && (
            <div
              ref={imageContainerRef}
              className={styles.container__right__imageContainer}
              onMouseEnter={handleImageContainerMouseEnter}
              onMouseMove={e => {
                handleImageContainerMouseMove({
                  eventData: { clientX: e.clientX, clientY: e.clientY },
                  getPxFromRem,
                  imageContainerRef,
                  hoverContainerRef,
                  sideBoxImageContainerRef,
                  sideBoxParentContainerRef,
                })
              }}
              onMouseLeave={() => {
                handleImageContainerMouseLeave({
                  hoverContainerRef,
                  sideBoxImageContainerRef,
                  sideBoxParentContainerRef,
                })
              }}
            >
              <Image
                alt={mainMedia.alt ?? mainMedia.title}
                {...ImageDomain.makeImageProps(mainMedia)}
                {...(pdpHeroImageId ? { id: pdpHeroImageId } : null)}
                // @i18n-ignore
                loading={'eager'}
                // @i18n-ignore
                fetchPriority={!isMobile && index === 0 ? 'high' : 'auto'}
                hasDynamicImage
              />
              {renderMainImageOverlay ? renderMainImageOverlay(index) : null}
              {isHoverContainerEnabled && hideProductThumbnails ? null : (
                <div className={styles.container__right__hoverContainer} ref={hoverContainerRef} />
              )}
            </div>
          )}
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {mainMedia?.url.includes('video') && isVideo(mainMedia) && (
            <Video
              {...VideoDomain.makeVideoProps(mainMedia)}
              loop
              muted
              controls
              preload={VIDEO_PRELOAD_TYPE.Metadata}
              className={styles.container__right__video}
              posterImage={
                selectedProductMedia && isProductVideo(selectedProductMedia)
                  ? selectedProductMedia.videoPoster
                  : undefined
              }
              autoplay
              tracker={`pdp-image-carousel__${videoIndex}`}
              trackingProperties={videoTrackingProperties}
            />
          )}
          {hideProductThumbnails ? (
            <>
              <button
                className={styles.chevronIconBtn}
                onClick={() => {
                  setActiveImage(index - 1)
                }}
                disabled={index === 0}
              >
                <ChevronIcon className={classnames(styles.chevronIcon, styles.chevronIconLeft)} />
              </button>
              <button
                className={styles.chevronIconBtn}
                onClick={() => {
                  setActiveImage(index + 1)
                }}
                disabled={index === media.length - 1}
              >
                <ChevronIcon className={classnames(styles.chevronIcon, styles.chevronIconRight)} />
              </button>
            </>
          ) : null}
        </div>

        {isHoverContainerEnabled &&
          !hideProductThumbnails &&
          mainMedia &&
          // eslint-disable-next-line i18next/no-literal-string
          !mainMedia.url.includes('video') &&
          isImage(mainMedia) && (
            <div className={classnames(styles.container__sideBoxParentContainer)} ref={sideBoxParentContainerRef}>
              <div
                ref={sideBoxImageContainerRef}
                className={styles.container__sideBoxParentContainer__sizeBoxImageContainer}
              >
                <Image
                  alt={mainMedia.alt ?? mainMedia.title}
                  {...ImageDomain.makeImageProps(mainMedia)}
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  // @i18n-ignore
                  loading={hasSelectedOptionImages && enableHighQualityImageDownloadOnHover ? 'eager' : 'lazy'}
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  quality={hasSelectedOptionImages && enableHighQualityImageDownloadOnHover ? 90 : 50}
                  hasDynamicImage
                />
              </div>
            </div>
          )}
      </div>

      <Modal
        open={showImagePopup}
        onClose={closeImagePopup}
        className={styles.mobileImagePopupModal}
        modalWrapperClassName={styles.mobileImagePopupModal_module_container}
        closeButtonClassName={styles.mobileImagePopupModal_close_button}
        showCloseButton
      >
        <Swiper
          key={id}
          className={styles.container__left__slider__modal}
          {...settings}
          numberOfVisibleSlides={1}
          afterChange={swiperInstance => {
            setActiveImage(swiperInstance.activeIndex)
          }}
          variableWidth
          infinite={{ loop: true, loopedSlides: 1 }}
          slideClassName={styles.container__left__slider__modal__sliderWrapper}
          initialSlide={index}
          // For modal we dont need horizontal caching.
          shouldCacheScrollPosition={false}
        >
          {media.map((med, index) => {
            const selectedProductMedia = media[index]
            const mainMedia =
              selectedProductMedia &&
              (isProductImage(selectedProductMedia) ? selectedProductMedia.image : selectedProductMedia.video)

            return (
              <div
                className={classnames(
                  styles.container__left__slider__modal__image,
                  media.length === 1 && styles.container__left__slider__singleImage,
                )}
                key={mainMedia?.url}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {isProductImage(med) && med.image && (
                  <Image
                    alt={med.image.alt ?? med.image.title}
                    {...ImageDomain.makeImageProps(med.image)}
                    // @i18n-ignore
                    loading={index === 0 ? 'eager' : undefined}
                    hasDynamicImage
                    maxWidth={getMaxWidthInPx({
                      maxWidthDesktopInRem: styles.modalImageWidthMobile as string,
                      maxWidthMobileInRem: styles.modalImageWidthMobile as string,
                      deviceType: deviceInfo.deviceType,
                    })}
                  />
                )}
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {isProductVideo(med) && med.video && (
                  <Video
                    {...VideoDomain.makeVideoProps(med.video)}
                    loop
                    muted
                    controls
                    preload={VIDEO_PRELOAD_TYPE.Auto}
                    loadingType={VIDEO_LOADING_TYPE.Eager}
                    className={styles.container__right__video__modal}
                    wrapperClassName={styles.popup_video_item_wrapper}
                    posterImage={
                      selectedProductMedia && isProductVideo(selectedProductMedia)
                        ? selectedProductMedia.videoPoster
                        : undefined
                    }
                    autoplay
                    tracker={`pdp-image-carousel__${videoIndex}`}
                    trackingProperties={videoTrackingProperties}
                  />
                )}
              </div>
            )
          })}
        </Swiper>
      </Modal>
    </>
  )
}

export default SliderImages
